import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ExclamationTriangleIcon,
  StarIcon,
  ClockIcon,
  EyeIcon,
  PlayIcon,
  ArchiveIcon,
  StopIcon,
  ArrowupIcon,
  ArrowDownIcon,
  ArrowRightIcon,
  ExclamationCircleIcon,
  FlagIcon,
  RocketIcon,
  QuestionCircleIcon,
} from '@nimbus-ds/icons'
import {
  Card,
  Button,
  Tag,
  Icon,
  Title,
  Text,
} from '@nimbus-ds/components'
import { experimentService } from '@/api/experiments'
import { funnelService } from '@/api/funnel'
import { authService } from '@/api/auth'
import {
  formatCurrency,
  formatPercentage,
} from '@/lib/utils'
import {
  buildAttentionQueue,
  projectCompletedExperimentMonthlyGain,
  type AttentionItem,
  type AttentionType,
} from '@/lib/economic-calculations'
import { ActiveExperimentCard } from '@/components/dashboard/ActiveExperimentCard'
import type { Experiment } from '@/types'

// ─── Attention Card Config ───────────────────────────────────────────

const ATTENTION_CONFIG: Record<
  AttentionType,
  {
    icon: React.ElementType
    label: string
    borderColor: string
    badgeClass: string
    description: (item: AttentionItem) => string
  }
> = {
  srm_warning: {
    icon: ExclamationTriangleIcon,
    label: 'Error de Muestreo',
    borderColor: 'border-l-red-500',
    badgeClass: 'bg-red-50 text-red-700 border-red-200',
    description: (item) =>
      item.srmWarning || 'Se detectó una distribución de tráfico anómala. Los resultados pueden no ser confiables.',
  },
  guardrail_veto: {
    icon: ExclamationTriangleIcon,
    label: 'Vetado por compras',
    borderColor: 'border-l-amber-500',
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
    description: (item) => {
      const conf = item.confidence != null ? ` (${formatPercentage(item.confidence * 100)})` : ''
      return `Gana en el objetivo pero vende menos${conf}. El guardarraíl de compras bloquea la decisión: no implementar sin revisar el objetivo.`
    },
  },
  guardrail_escalate: {
    icon: StarIcon,
    label: 'Decidido por compras',
    borderColor: 'border-l-green-500',
    badgeClass: 'bg-green-50 text-green-700 border-green-200',
    description: (item) => {
      const conf = item.confidence != null ? `${formatPercentage(item.confidence * 100)} de probabilidad` : 'alta probabilidad'
      const who = item.guardrailHypothesisBetter ? 'la hipótesis' : 'la línea base'
      return `El objetivo no discrimina, pero las compras sí: ${conf} de que ${who} venda más. Listo para decidir por el guardarraíl.`
    },
  },
  winner_ready: {
    icon: StarIcon,
    label: 'Ganador Detectado',
    borderColor: 'border-l-green-500',
    badgeClass: 'bg-green-50 text-green-700 border-green-200',
    description: (item) => {
      const lift = item.liftPercentage != null ? `+${formatPercentage(item.liftPercentage)}` : ''
      const conf = item.confidence != null ? ` con ${formatPercentage(item.confidence * 100)} de confianza` : ''
      return `La variante ganadora muestra un lift de ${lift}${conf}. Listo para implementar.`
    },
  },
  low_traffic: {
    icon: ExclamationCircleIcon,
    label: 'Tráfico Bajo',
    borderColor: 'border-l-amber-500',
    badgeClass: 'bg-amber-50 text-amber-700 border-amber-200',
    description: (item) =>
      `Después de ${item.daysRunning} días, solo se alcanzó el ${formatPercentage(item.samplePercentage ?? 0)} de la muestra necesaria.`,
  },
  underperformer: {
    icon: ArrowupIcon,
    label: 'Sin Mejora',
    borderColor: 'border-l-orange-500',
    badgeClass: 'bg-orange-50 text-orange-700 border-orange-200',
    description: (item) => {
      const days = item.daysRunning ?? 0
      const lift = item.liftPercentage != null ? formatPercentage(item.liftPercentage) : 'N/A'
      return `Después de ${days} días el lift es ${lift}. Considerá detener o iterar la hipótesis.`
    },
  },
  stale: {
    icon: ClockIcon,
    label: 'Experimento Pausado',
    borderColor: 'border-l-gray-400',
    badgeClass: 'bg-gray-50 text-gray-600 border-gray-200',
    description: (item) =>
      `Pausado hace ${item.daysSince} días sin cambios. Reanudá o archivá para mantener el orden.`,
  },
}

// Nimbus Tag appearance per attention type (presentation mapping for the
// former badgeClass semantic colors).
const ATTENTION_TAG_APPEARANCE: Record<
  AttentionType,
  'primary' | 'success' | 'warning' | 'danger' | 'neutral'
> = {
  srm_warning: 'danger',
  guardrail_veto: 'warning',
  guardrail_escalate: 'success',
  winner_ready: 'success',
  low_traffic: 'warning',
  underperformer: 'warning',
  stale: 'neutral',
}

// ─── Attention Card Component ────────────────────────────────────────

function AttentionCard({
  item,
  onNavigateResults,
  onNavigateDetails,
  onPause,
  onResume,
  onArchive,
}: {
  item: AttentionItem
  onNavigateResults: (id: string) => void
  onNavigateDetails: (id: string) => void
  onPause: (id: string) => void
  onResume: (id: string) => void
  onArchive: (id: string) => void
}) {
  const config = ATTENTION_CONFIG[item.type]
  const TypeIcon = config.icon

  return (
    <div className={`flex items-start gap-4 rounded-lg border border-l-4 ${config.borderColor} bg-card p-4`}>
      <div className="mt-0.5 shrink-0 text-muted-foreground">
        <TypeIcon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Title as="h3">{item.experiment.name}</Title>
          <div className="shrink-0 whitespace-nowrap">
            <Tag appearance={ATTENTION_TAG_APPEARANCE[item.type]}>
              {config.label}
            </Tag>
          </div>
        </div>
        <Text className="mt-1">
          {config.description(item)}
        </Text>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {(item.type === 'winner_ready' || item.type === 'guardrail_escalate') && (
          <>
            <Button onClick={() => onNavigateResults(item.experiment.id)}>
              <Icon source={<EyeIcon />} color="currentColor" />
              Ver Resultados
            </Button>
          </>
        )}
        {item.type === 'guardrail_veto' && (
          <>
            <Button appearance="neutral" onClick={() => onNavigateResults(item.experiment.id)}>
              <Icon source={<EyeIcon />} color="currentColor" />
              Revisar
            </Button>
            <Button appearance="danger" onClick={() => onPause(item.experiment.id)}>
              <Icon source={<StopIcon />} color="currentColor" />
              Detener
            </Button>
          </>
        )}
        {item.type === 'underperformer' && (
          <>
            <Button appearance="neutral" onClick={() => onNavigateResults(item.experiment.id)}>
              <Icon source={<EyeIcon />} color="currentColor" />
              Revisar
            </Button>
            <Button appearance="danger" onClick={() => onPause(item.experiment.id)}>
              <Icon source={<StopIcon />} color="currentColor" />
              Detener
            </Button>
          </>
        )}
        {item.type === 'low_traffic' && (
          <Button appearance="neutral" onClick={() => onNavigateDetails(item.experiment.id)}>
            <Icon source={<FlagIcon />} color="currentColor" />
            Revisar Segmentación
          </Button>
        )}
        {item.type === 'srm_warning' && (
          <Button appearance="danger" onClick={() => onNavigateResults(item.experiment.id)}>
            <Icon source={<ExclamationTriangleIcon />} color="currentColor" />
            Investigar
          </Button>
        )}
        {item.type === 'stale' && (
          <>
            <Button appearance="neutral" onClick={() => onResume(item.experiment.id)}>
              <Icon source={<PlayIcon />} color="currentColor" />
              Reanudar
            </Button>
            <Button appearance="transparent" onClick={() => onArchive(item.experiment.id)}>
              <Icon source={<ArchiveIcon />} color="currentColor" />
              Archivar
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Recent Win/Loss Card ────────────────────────────────────────────

function RecentResultCard({
  experiment,
  storeAOV,
  onClick,
}: {
  experiment: Experiment
  storeAOV: number
  onClick: () => void
}) {
  const summary = experiment.results_summary

  // The lambda only sets `summary.winner` when a pairwise reached significance,
  // so a non-null winner is the source of truth for "has winner" — independent
  // of the top-level `is_significant` flag (which used to drift in multi-variant
  // tests, see lambda/aggregation-pipeline/index.js:922).
  const winnerKey = summary?.winner ?? null
  const hasWinner = Boolean(winnerKey)

  // In a negative experiment the hypothesis is the control variant and the
  // baseline is treatment — so a control win confirms the hypothesis.
  const isNegative = experiment.is_negative === true
  const hypothesisKey = isNegative ? 'control' : 'treatment'
  const baselineKey = isNegative ? 'treatment' : 'control'
  const hypothesisWon = winnerKey === hypothesisKey

  // Lift framed from the hypothesis perspective, recomputed against the baseline
  // variant so the displayed % matches the verdict (the frozen `summary.lift`
  // is always treatment-vs-control regardless of experiment polarity).
  let hypothesisLift = summary?.lift?.percentage ?? 0
  const hv = summary?.variants?.[hypothesisKey]
  const bv = summary?.variants?.[baselineKey]
  if (hv && bv && bv.conversion_rate > 0) {
    hypothesisLift = ((hv.conversion_rate - bv.conversion_rate) / bv.conversion_rate) * 100
  }

  const verdictLabel = isNegative
    ? (hypothesisWon
        ? `Hipótesis confirmada · +${formatPercentage(hypothesisLift)} de lift`
        : 'Hipótesis refutada · el cambio no mejoró los resultados')
    : (hypothesisWon
        ? `Tratamiento ganó con +${formatPercentage(hypothesisLift)} de lift`
        : 'Control ganó · el tratamiento no mejoró')

  const projection = hasWinner
    ? projectCompletedExperimentMonthlyGain(experiment, storeAOV)
    : null

  return (
    <div
      className="flex cursor-pointer items-center justify-between rounded-lg border bg-card p-4 transition-colors hover:bg-muted/50"
      onClick={onClick}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
          hypothesisWon ? 'bg-green-50' : hasWinner ? 'bg-amber-50' : 'bg-gray-100'
        }`}>
          {hypothesisWon
            ? <StarIcon className="h-4 w-4 text-green-600" />
            : hasWinner
              ? <ArrowDownIcon className="h-4 w-4 text-amber-600" />
              : <QuestionCircleIcon className="h-4 w-4 text-gray-400" />}
        </div>
        <div className="min-w-0">
          <Title as="h4" className="truncate">{experiment.name}</Title>
          {hasWinner ? (
            <p className={`text-xs ${hypothesisWon ? 'text-green-600' : 'text-amber-600'}`}>
              {verdictLabel}
            </p>
          ) : (
            <Text fontSize="caption">Sin ganador &mdash; inconcluso</Text>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {projection && projection.projectedMonthlyGain > 0 && (
          <span className="text-xs font-medium tabular-nums text-green-600">
            +{formatCurrency(projection.projectedMonthlyGain)} /mes
          </span>
        )}
        <ArrowRightIcon className="h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  )
}


// ─── Loading Skeleton ────────────────────────────────────────────────

function DashboardSkeleton() {
  return (
    <div className="space-y-8">
      {/* Header skeleton */}
      <div>
        <div className="h-8 w-48 animate-pulse rounded bg-muted" />
        <div className="mt-2 h-4 w-72 animate-pulse rounded bg-muted" />
      </div>
      {/* Attention placeholder */}
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
      {/* Grid placeholder */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-44 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    </div>
  )
}

// ─── Main Dashboard ──────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = useNavigate()

  const [experiments, setExperiments] = useState<Experiment[]>([])
  const [storeAOV, setStoreAOV] = useState(0)
  const [loading, setLoading] = useState(true)

  const [resultsMap, setResultsMap] = useState<Map<string, any>>(new Map())
  const [resultsLoading, setResultsLoading] = useState(false)

  // Phase 1: Load experiments + store analytics
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const storeId = authService.getCurrentStoreId()

        const [expResult, storeResult] = await Promise.allSettled([
          experimentService.getExperiments(),
          storeId ? funnelService.getStoreFunnelAnalytics(storeId, 30) : Promise.resolve(null),
        ])

        if (expResult.status === 'fulfilled' && Array.isArray(expResult.value)) {
          setExperiments(expResult.value)
        }

        if (storeResult.status === 'fulfilled' && storeResult.value) {
          const analytics = storeResult.value as any
          const tnx = analytics?.tnx_analytics ?? analytics
          const revenue = tnx?.revenue ?? 0
          const conversions = tnx?.conversions ?? 0
          setStoreAOV(conversions > 0 ? revenue / conversions : 0)
        }
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // Phase 2: Load results for active + paused experiments
  useEffect(() => {
    const needsResults = experiments.filter(
      (e) => e.status === 'active' || e.status === 'paused'
    )
    if (needsResults.length === 0) return

    const loadResults = async () => {
      setResultsLoading(true)
      try {
        const settled = await Promise.allSettled(
          needsResults.map((e) => experimentService.getExperimentResults(e.id))
        )

        const map = new Map<string, any>()
        settled.forEach((result, i) => {
          if (result.status === 'fulfilled' && result.value) {
            map.set(needsResults[i].id, result.value)
          }
        })
        setResultsMap(map)
      } finally {
        setResultsLoading(false)
      }
    }
    loadResults()
  }, [experiments])

  // Derived data
  const attentionQueue = buildAttentionQueue(experiments, resultsMap)
  const activeExperiments = experiments.filter((e) => e.status === 'active')
  const completedExperiments = experiments
    .filter((e) => e.status === 'completed')
    .sort((a, b) => {
      const dateA = a.finalized_at || a.updated_at
      const dateB = b.finalized_at || b.updated_at
      return new Date(dateB).getTime() - new Date(dateA).getTime()
    })
    .slice(0, 5)

  // Action handlers
  const refreshExperiments = useCallback(async () => {
    try {
      const data = await experimentService.getExperiments()
      if (Array.isArray(data)) setExperiments(data)
    } catch {
      // silent
    }
  }, [])

  const handlePause = useCallback(async (id: string) => {
    try {
      await experimentService.pauseExperiment(id)
      await refreshExperiments()
    } catch {
      // silent
    }
  }, [refreshExperiments])

  const handleResume = useCallback(async (id: string) => {
    try {
      await experimentService.resumeExperiment(id)
      await refreshExperiments()
    } catch {
      // silent
    }
  }, [refreshExperiments])

  const handleArchive = useCallback(async (id: string) => {
    try {
      await experimentService.updateExperiment(id, { status: 'archived' } as any)
      await refreshExperiments()
    } catch {
      // silent
    }
  }, [refreshExperiments])

  if (loading) return <DashboardSkeleton />

  const hasExperiments = experiments.length > 0

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Title as="h1">Centro de Control</Title>
          <Text>
            Decisiones pendientes, rendimiento activo y proyección por experimento
          </Text>
        </div>
        <Button onClick={() => navigate('/dashboard/experiments')}>
          <Icon source={<RocketIcon />} color="currentColor" />
          Crear Experimento
        </Button>
      </div>

      {/* Section 1: Attention Queue */}
      {attentionQueue.length > 0 && (
        <section className="space-y-3">
          <Title as="h2">Cola de Atención</Title>
          {attentionQueue.map((item, i) => (
            <AttentionCard
              key={`${item.experiment.id}-${item.type}-${i}`}
              item={item}
              onNavigateResults={(id) => navigate(`/dashboard/experiments/${id}/results`)}
              onNavigateDetails={(id) => navigate(`/dashboard/experiments/${id}`)}
              onPause={handlePause}
              onResume={handleResume}
              onArchive={handleArchive}
            />
          ))}
        </section>
      )}

      {/* Section 2: Active Experiments */}
      <section className="space-y-4">
        <Title as="h2">Experimentos Activos</Title>
        {activeExperiments.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {activeExperiments.map((exp) => (
              <ActiveExperimentCard
                key={exp.id}
                experiment={exp}
                results={resultsMap.get(exp.id) ?? null}
                storeAOV={storeAOV}
                onClick={() => navigate(`/dashboard/experiments/${exp.id}/results`)}
              />
            ))}
          </div>
        ) : (
          <Card>
            <div className="py-8 text-center">
              <RocketIcon className="mx-auto h-10 w-10 text-muted-foreground" />
              <Text className="mt-3">
                No hay experimentos activos. Creá uno para empezar a optimizar.
              </Text>
              <Button
                className="mt-4"
                appearance="neutral"
                onClick={() => navigate('/dashboard/experiments')}
              >
                Crear Experimento
              </Button>
            </div>
          </Card>
        )}
        {resultsLoading && activeExperiments.length > 0 && (
          <Text fontSize="caption" className="animate-pulse">Cargando métricas...</Text>
        )}
      </section>

      {/* Section 3: Recent Wins & Losses */}
      {completedExperiments.length > 0 && (
        <section className="space-y-3">
          <Title as="h2">Resultados Recientes</Title>
          <div className="space-y-2">
            {completedExperiments.map((exp) => (
              <RecentResultCard
                key={exp.id}
                experiment={exp}
                storeAOV={storeAOV}
                onClick={() => navigate(`/dashboard/experiments/${exp.id}/results`)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Empty state: no experiments at all */}
      {!hasExperiments && (
        <Card>
          <div className="py-12 text-center">
            <FlagIcon className="mx-auto h-12 w-12 text-muted-foreground" />
            <Title as="h3" className="mt-4">Bienvenido a TN-Xperiment</Title>
            <Text className="mt-2">
              Creá tu primer experimento A/B para empezar a optimizar tu tienda.
            </Text>
            <Button
              className="mt-4"
              onClick={() => navigate('/dashboard/experiments')}
            >
              Crear Experimento
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}
